import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Button, alpha, Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getSenateQonDetail } from '../services/api';

// Senate red — matches NoticePapers / motion detail when chamber=Senate.
const THEME_COLOR = '#c62828';

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

export default function SenateQonDetail() {
  const { id } = useParams();
  const [qon, setQon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchQon = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getSenateQonDetail(id);
        if (!cancelled) setQon(data);
      } catch (err) {
        console.error('Error fetching Senate QoN:', err);
        if (!cancelled) setError('Failed to load Senate Question on Notice');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchQon();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: THEME_COLOR }} />
      </Box>
    );
  }

  if (error || !qon) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error || 'Question not found'}</Typography>
        <Button component={RouterLink} to="/senateqon" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Senate Questions on Notice
        </Button>
      </Box>
    );
  }

  const headlineTitle = qon.title
    ? qon.title
    : `Question${qon.question_number ? ` ${qon.question_number}` : ''}`;

  return (
    <Box>
      {/* Back link */}
      <Box sx={{ mb: 2 }}>
        <Button
          component={RouterLink}
          to="/senateqon"
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to Senate Questions on Notice
        </Button>
      </Box>

      {/* Hero */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(THEME_COLOR, 0.08)} 0%, ${alpha(THEME_COLOR, 0.02)} 100%)`,
          border: '1px solid',
          borderColor: alpha(THEME_COLOR, 0.15)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <HelpOutlineIcon sx={{ fontSize: 48, color: THEME_COLOR }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="overline" sx={{ color: THEME_COLOR, fontWeight: 600 }}>
              Senate Question on Notice{qon.question_number ? ` · Q${qon.question_number}` : ''}
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: THEME_COLOR, mb: 0.5 }}
            >
              {headlineTitle}
            </Typography>

            {qon.asked_date && (
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                Asked {formatDate(qon.asked_date)}
              </Typography>
            )}

            {/* Metadata chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {qon.status && (
                <Chip
                  label={qon.status}
                  color={qon.status === 'Answered' ? 'success' : 'warning'}
                  size="small"
                />
              )}
              {qon.part && (
                <Chip label={`Portfolio: ${qon.part}`} size="small" variant="outlined" />
              )}
            </Box>

            {/* Asker / Responder */}
            <Box sx={{ mb: 2 }}>
              {qon.asked_by && (
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  <strong>Asked by:</strong>{' '}
                  {qon.asked_by_id ? (
                    <RouterLink
                      to={`/parliamentarians/${qon.asked_by_id}`}
                      style={{ color: THEME_COLOR, textDecoration: 'none' }}
                    >
                      {qon.asked_by}
                    </RouterLink>
                  ) : (
                    qon.asked_by
                  )}
                </Typography>
              )}
              {qon.asked_to && (
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  <strong>Asked to:</strong> {qon.asked_to}
                </Typography>
              )}
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {qon.source_url && (
                <Button
                  size="small"
                  variant="outlined"
                  href={qon.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon />}
                  sx={{ borderColor: THEME_COLOR, color: THEME_COLOR }}
                >
                  View on ParlWork
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Question body */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: THEME_COLOR }}>
          Question
        </Typography>
        {qon.content_html ? (
          <Box
            sx={{
              '& p': { mb: 2, lineHeight: 1.8 },
              '& ol, & ul': { pl: 3, my: 1 },
              '& li': { mb: 0.5 },
              '& mark': { bgcolor: 'yellow', color: 'inherit' },
              lineHeight: 1.8,
              fontSize: '0.95rem'
            }}
            dangerouslySetInnerHTML={{ __html: qon.content_html }}
          />
        ) : qon.content ? (
          <Typography
            component="pre"
            sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              m: 0,
              lineHeight: 1.8,
              fontSize: '0.95rem'
            }}
          >
            {qon.content}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No question text available.
          </Typography>
        )}
      </Paper>

      {/* Answer body (if present) */}
      {(qon.answer_html || qon.answer || qon.response_html || qon.response) && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: THEME_COLOR }}>
            Answer
          </Typography>
          {(qon.answer_html || qon.response_html) ? (
            <Box
              sx={{
                '& p': { mb: 2, lineHeight: 1.8 },
                '& ol, & ul': { pl: 3, my: 1 },
                '& li': { mb: 0.5 },
                lineHeight: 1.8,
                fontSize: '0.95rem'
              }}
              dangerouslySetInnerHTML={{ __html: qon.answer_html || qon.response_html }}
            />
          ) : (
            <Typography
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                m: 0,
                lineHeight: 1.8,
                fontSize: '0.95rem'
              }}
            >
              {qon.answer || qon.response}
            </Typography>
          )}
          {qon.answered_date && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Answered {formatDate(qon.answered_date)}
            </Typography>
          )}
        </Paper>
      )}

      {/* Source */}
      {qon.source_url && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Source:{' '}
          <Link href={qon.source_url} target="_blank" rel="noopener noreferrer">
            ParlWork
          </Link>
          {qon.source && ` (${qon.source})`}
        </Typography>
      )}
    </Box>
  );
}
